<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Icon from '@/components/Icon.vue'
import { matrix } from '@/lib/matrix'
import { initials as toInitials } from '@/lib/util'
import { fmtTime } from '@/lib/dates'
import { modeMeta, type Person } from '@/types/atrium'

const props = withDefaults(
  defineProps<{ person: Person; size?: number; ring?: boolean; acc?: boolean; tip?: boolean }>(),
  { size: 40, ring: true, acc: true, tip: true },
)

const mode = computed(() => modeMeta(props.person.mode))
const ini = computed(() => toInitials(props.person.name))

// Custom avatar image: media download is authenticated, so resolve the mxc://
// to a token-fetched object URL (cached in the client). Initials render until
// it arrives — and stay if it never does.
const imgUrl = ref<string | null>(null)
watch(
  () => props.person.avatarUrl,
  async (mxc) => {
    imgUrl.value = null
    if (mxc) imgUrl.value = await matrix.mediaObjectUrl(mxc)
  },
  { immediate: true },
)
const away = computed(() => props.person.mode === 'away')
const accIcon = computed(() => (props.person.accessory === 'headphones' ? 'headphones' : 'coffee'))
// Their Google Calendar says a meeting is on right now (§10.4).
const meeting = computed(() => props.person.meeting ?? null)
const meetingTip = computed(() => {
  const m = meeting.value
  if (!m) return ''
  const what = m.summary ? `“${m.summary}”` : 'a meeting'
  return m.end ? `In ${what} until ${fmtTime(m.end)}` : `In ${what}`
})
const tipText = computed(() => {
  const base = `${props.person.name} · ${mode.value.label}`
  return meeting.value ? `${base} — ${meetingTip.value}` : base
})
</script>

<template>
  <span
    class="av"
    :class="{ 'is-away': away, 'av--ring': ring }"
    :style="`--sz:${size}px;--c:${person.color};--pc:${mode.color};--pg:${mode.glow}`"
    :data-tip="tip ? tipText : undefined"
  >
    <img v-if="imgUrl" class="av__img" :src="imgUrl" alt="" draggable="false" @error="imgUrl = null" />
    <span v-else class="av__initials">{{ ini }}</span>
    <span v-if="meeting" class="av__cal" :data-tip="tip ? undefined : meetingTip"><Icon name="cal2" :size="11" /></span>
    <span v-else-if="acc && person.accessory" class="av__acc"><Icon :name="accIcon" :size="14" /></span>
    <span v-if="person.guest" class="av__guest" :data-tip="`Guest · ${person.server || ''}`"><Icon name="globe" :size="14" /></span>
    <span v-if="ring" class="av__ring" />
  </span>
</template>
